import type { BillLine } from './types';

export function lineGross(line: BillLine): number {
  const [, qty, price] = line;
  return Math.round((price * qty) / 1000);
}

export function lineNet(line: BillLine): number {
  return lineGross(line) - line[3];
}

/** GST included in an inclusive amount. */
export function inclusiveTax(amount: number, taxBp: number): number {
  if (taxBp <= 0) return 0;
  return Math.round((amount * taxBp) / (10000 + taxBp));
}

export interface Totals {
  subtotal: number; // sum of gross lines
  discount: number; // line + bill discounts
  tax: number;      // GST included
  round: number;    // rounding to nearest rupee
  total: number;
  units: number;    // sum of qty (milli)
}

/**
 * Totals for GST-inclusive prices. The bill discount is spread across lines
 * proportionally so tax stays correct per rate.
 */
export function computeTotals(lines: BillLine[], billDiscount = 0, roundToRupee = true): Totals {
  const subtotal = lines.reduce((s, l) => s + lineGross(l), 0);
  const lineDisc = lines.reduce((s, l) => s + l[3], 0);
  const afterLine = subtotal - lineDisc;
  const billDisc = Math.min(Math.max(0, billDiscount), Math.max(0, afterLine));
  let tax = 0;
  for (const l of lines) {
    const net = lineNet(l);
    const share = afterLine > 0 ? Math.round((billDisc * net) / afterLine) : 0;
    tax += inclusiveTax(net - share, l[4]);
  }
  const exact = afterLine - billDisc;
  const total = roundToRupee ? Math.round(exact / 100) * 100 : exact;
  return {
    subtotal,
    discount: lineDisc + billDisc,
    tax,
    round: total - exact,
    total,
    units: lines.reduce((s, l) => s + l[1], 0),
  };
}

/** Time-ordered UUIDv7 (RFC 9562). */
export function uuidv7(now = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let ts = now;
  for (let i = 5; i >= 0; i--) {
    bytes[i] = ts % 256;
    ts = Math.floor(ts / 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function billNumber(deviceCode: string, devNo: number): string {
  return `${deviceCode}-${String(devNo).padStart(6, '0')}`;
}

export const DEFAULT_BILL_TEMPLATE = '{DEVICE}-{SEQ:6}';

/**
 * Bill number from a store template. Tokens: {STORE} store number, {DEVICE} terminal code,
 * {YYYY} {YY} {MM} {DD} bill date, {SEQ} or {SEQ:n} the terminal's running number (padded to n).
 * `start` lifts every terminal's sequence (e.g. continue from an old POS); the counter itself never resets.
 */
export function formatBillNo(template: string | undefined, v: { store: string; device: string; seq: number; ts: number; start?: number }): string {
  const t = template?.includes('{SEQ') ? template : DEFAULT_BILL_TEMPLATE;
  const d = new Date(v.ts);
  const seq = v.seq + Math.max(0, (v.start ?? 0) - 1);
  return t
    .replace(/\{SEQ(?::(\d))?\}/g, (_, n) => String(seq).padStart(n ? Number(n) : 1, '0'))
    .replace(/\{STORE\}/g, v.store).replace(/\{DEVICE\}/g, v.device)
    .replace(/\{YYYY\}/g, String(d.getFullYear())).replace(/\{YY\}/g, String(d.getFullYear()).slice(2))
    .replace(/\{MM\}/g, String(d.getMonth() + 1).padStart(2, '0')).replace(/\{DD\}/g, String(d.getDate()).padStart(2, '0'))
    .slice(0, 40);
}

/** Loyalty: 1 point per full ₹100 of a sale. */
/** Loyalty points keep accruing on the server, but every points screen stays hidden until this is switched on. */
export const LOYALTY_ENABLED = false;
export const POINTS_PER_RUPEES = 100;
export function pointsFor(totalPaise: number): number {
  return Math.max(0, Math.floor(totalPaise / (POINTS_PER_RUPEES * 100)));
}
